import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Button,
  Tooltip,
  Slider,
  Paper,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Profile } from '@par/shared/types/player';
import { TabLabel } from './TabLabel';
import { TabPanel } from './TabPanel';
import { BannerAvailableMatches } from './BannerAvailableMatches';
import { StyledTypographyCredits } from './mui-styles';

const a11yProps = (index: number) => {
  return {
    id: `solo-tab-${index}`,
    'aria-controls': `solo-tabpanel-${index}`,
  };
};

type PropTypes = {
  expandedSearchRange: number[];
  matchesAvailable: number;
  profile?: Profile;
  onViewHandicapWinRatesClick: () => void;
  handleSearchOption: (
    event: React.MouseEvent<HTMLElement>,
    value: 'mmr' | 'handicap'
  ) => void;
  handleSearchSliderChange: (
    event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => void;
};

export const PlayMenuTabsAsync1v1 = ({
  expandedSearchRange,
  matchesAvailable,
  profile,
  handleSearchOption,
  handleSearchSliderChange,
  onViewHandicapWinRatesClick,
}: PropTypes) => {
  const expandSearchMarks = [
    {
      value: profile.rankedRating - 250,
      label: `${profile.rankedRating - 250}`,
    },

    {
      value: profile.rankedRating,
      label: `${profile.rankedRating}`,
    },

    {
      value: profile.rankedRating + 250,
      label: `${profile.rankedRating + 250}`,
    },
  ];

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    handleSearchOption(
      event as React.MouseEvent<HTMLElement>,
      newValue === 0 ? 'mmr' : 'handicap'
    );
  };

  return (
    <>
      <Box
        sx={{
          marginTop: '0px !important',
          backgroundColor: '#f9f9f90d',
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="multiplayer tabs"
          indicatorColor="secondary"
          centered
          sx={{ width: '100%', flex: 1 }}
        >
          <Tab
            label={
              <TabLabel text="Similarily Matched" isActiveTab={value === 0} />
            }
            {...a11yProps(0)}
            sx={{ flex: 1, maxWidth: '50%' }}
          />
          <Tab
            label={<TabLabel text="Handicapped" isActiveTab={value === 1} />}
            {...a11yProps(1)}
            sx={{ flex: 1, maxWidth: '50%' }}
          />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <StyledTypographyCredits variant="h6">
          Search credits left: {profile.seedMatch || 0}
        </StyledTypographyCredits>

        <BannerAvailableMatches matchesAvailable={matchesAvailable} />
        <Stack spacing={3} alignItems={'center'} justifyContent={'center'}>
          <Paper sx={{ width: '50%', paddingX: 3, paddingY: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems={'center'}
              justifyContent={'center'}
              mb={2}
            >
              <Tooltip
                title={
                  'You can expand the search by increasing your MMR range. The default is set to +/- 100 from your current MMR'
                }
                placement="top"
              >
                <InfoIcon
                  sx={{
                    fontSize: 20,
                  }}
                />
              </Tooltip>
              <Typography variant="subtitle1">
                Adjust MMR search range
              </Typography>
            </Stack>
            <Slider
              defaultValue={100}
              value={expandedSearchRange}
              onChange={handleSearchSliderChange}
              valueLabelDisplay="auto"
              step={50}
              marks={expandSearchMarks}
              min={profile.rankedRating - 250}
              max={profile.rankedRating + 250}
              disableSwap
              sx={{ width: '100%' }}
            />
          </Paper>
        </Stack>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <StyledTypographyCredits variant="h6">
          Search credits left: {profile.seedMatch || 0}
        </StyledTypographyCredits>

        <BannerAvailableMatches matchesAvailable={matchesAvailable} />

        <Paper sx={{ alignSelf: 'center', paddingX: 3, paddingY: 2 }}>
          <Stack spacing={2} alignItems={'center'}>
            <Typography variant="subtitle2" textAlign={'center'} px={10}>
              If your MMR is above 1500, you'll match with opponents of 1500 MMR
              or higher. The reverse if below 1500. A handicap is applied to
              your starting score based on the MMR difference. The maximum MMR
              change is ±10.
            </Typography>
            <Button onClick={onViewHandicapWinRatesClick}>
              View Your handicap win rates
            </Button>
          </Stack>
        </Paper>
      </TabPanel>
    </>
  );
};
